import axios from 'axios'
import aqe from '@pushly/aqe'
import { Singleton } from 'typescript-ioc/es5'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AppService } from './app.service'
import { Container } from 'typescript-ioc/es5'
import querystring from 'query-string'
import { IUserRole } from '../interfaces/user-role'

@Singleton
class UserRoleService {
    protected readonly appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<IUserRole[]>> {
        const response: IApiResponse<IUserRole[]> = {
            ok: false,
            data: [],
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const options = querystring.stringify(opts.query || {})
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v3/access-control/user-roles?${options}`)

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('user_role_fetch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async fetch(id: number, opts: IApiCallOptions = {}): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<IUserRole> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const options = querystring.stringify(opts.query || {})
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v3/access-control/user-roles/${id}?${options}`)

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('user_role_fetch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async patch(
        id: number,
        updates: Partial<IUserRole>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<IUserRole> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axios.patch(`${aqe.defaults.publicApiDomain}/access-control/user-roles/${id}`, {
                id,
                ...updates,
            })

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('user_role_patch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async attachAccessPolicyIds(
        id: number,
        policyIds: number[],
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<null> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/access-control/user-roles/${id}/attach-access-policies`, {
                access_policy_ids: policyIds,
            })

            response.ok = true
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('user_role_attach_policies_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async removeAccessPolicyIds(
        id: number,
        policyIds: number[],
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<null> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/access-control/user-roles/${id}/remove-access-policies`, {
                access_policy_ids: policyIds,
            })

            response.ok = true
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('user_role_remove_policies_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }
}

export default UserRoleService
